import { useMemo, useState, useEffect } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { eur } from "@/lib/api";
import { X, Plus, Minus, Check, Heart } from "lucide-react";

export default function ProductModal({ product, open, onClose, onAdd, t, theme = { primary: "#123026", gold: "#C69C4A" } }) {
  const [single, setSingle] = useState({});   // groupId -> value name
  const [multi, setMulti] = useState({});      // groupId -> [value names]
  const [courses, setCourses] = useState({});  // courseId -> choice name
  const [qty, setQty] = useState(1);
  const [instructions, setInstructions] = useState("");

  const groups = product?.option_groups || [];
  const formula = product?.formula_courses || [];

  useEffect(() => {
    if (!open) return;
    setSingle({}); setMulti({}); setCourses({}); setQty(1); setInstructions("");
  }, [open, product?.id]);

  const priceOfValue = (g, name) => (g.values.find((v) => v.name === name)?.price || 0);

  const unitPrice = useMemo(() => {
    if (!product) return 0;
    let extra = 0;
    groups.forEach((g) => {
      if (g.type === "single" && single[g.id]) extra += priceOfValue(g, single[g.id]);
      if (g.type === "multi") (multi[g.id] || []).forEach((n) => { extra += priceOfValue(g, n); });
    });
    formula.forEach((c) => {
      const ch = (c.options || []).find((o) => o.name === courses[c.id]);
      if (ch) extra += ch.price || 0;
    });
    return product.price + extra;
  }, [product, groups, formula, single, multi, courses]);

  if (!product) return null;

  const missing = [
    ...groups.filter((g) => g.required && (g.type === "single" ? !single[g.id] : !(multi[g.id] || []).length)),
    ...formula.filter((c) => c.required && !courses[c.id]),
  ];
  const canAdd = missing.length === 0;

  const toggleMulti = (gid, name) => setMulti((m) => {
    const cur = m[gid] || [];
    return { ...m, [gid]: cur.includes(name) ? cur.filter((x) => x !== name) : [...cur, name] };
  });

  const handleAdd = () => {
    const options = [];
    groups.forEach((g) => {
      if (g.type === "single" && single[g.id]) options.push({ group: g.name, value: single[g.id], price: priceOfValue(g, single[g.id]) });
      if (g.type === "multi") (multi[g.id] || []).forEach((n) => options.push({ group: g.name, value: n, price: priceOfValue(g, n) }));
    });
    const formulaSel = formula.map((c) => {
      const ch = (c.options || []).find((o) => o.name === courses[c.id]);
      return { course: c.name, choice: courses[c.id], price: ch?.price || 0 };
    }).filter((f) => f.choice);
    onAdd({
      product_id: product.id, name: product.name, image: product.image, base_price: product.price,
      quantity: qty, options, formula: formulaSel, cooking: null, side: null, supplements: [],
      instructions: instructions.trim() || null, unit_price: unitPrice, line_total: unitPrice * qty,
      category: product.category,
    });
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="product-modal" className="p-0 gap-0 max-w-lg w-[calc(100%-1.5rem)] rounded-3xl overflow-hidden border border-[#EAE5DC] max-h-[92vh] flex flex-col bg-[#FBF9F5] shadow-2xl">
        <DialogTitle className="sr-only">{product.name}</DialogTitle>
        <DialogDescription className="sr-only">Personnalisez {product.name}</DialogDescription>

        {/* Immersive hero */}
        <div className="relative h-60 sm:h-64 shrink-0 overflow-hidden bg-[#123026]">
          {product.image ? (
            <img src={product.image} alt={product.name} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full grid place-items-center bg-[#EFEADF]">
              <span className="font-serif-display text-6xl text-[#B69A6B]">{(product.name || "?").trim().charAt(0).toUpperCase()}</span>
            </div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/25 to-transparent" />
          <button data-testid="product-modal-close" onClick={onClose} className="absolute top-3.5 right-3.5 w-10 h-10 rounded-full bg-white/80 backdrop-blur-md border border-white/60 grid place-items-center text-[#123026] hover:bg-white transition-all shadow-md">
            <X className="w-5 h-5" />
          </button>
          {product.is_popular && (
            <span className="absolute top-3.5 left-3.5 inline-flex items-center gap-1.5 rounded-full bg-[#C69C4A] text-white text-xs font-bold px-3 py-1 shadow-lg">
              <Heart className="w-3 h-3 fill-white" /> {t.popular}
            </span>
          )}
          <div className="absolute bottom-4 left-5 right-5">
            <h2 className="font-serif-display text-2xl sm:text-3xl text-white font-bold leading-tight drop-shadow-md">{product.name}</h2>
          </div>
        </div>

        <div className="overflow-y-auto no-scrollbar px-6 pt-5 pb-4 flex-1 space-y-6">
          <div>
            <p className="text-[#5C615E] text-sm leading-relaxed">{product.description}</p>
            <p className="font-serif-display text-lg font-bold text-[#123026] mt-2">{eur(product.price)}</p>
          </div>

          {/* Formula courses */}
          {formula.map((c) => (
            <Section key={c.id} title={c.name} required={c.required}>
              <div className="flex flex-wrap gap-2">
                {(c.options || []).map((o) => {
                  const active = courses[c.id] === o.name;
                  return (
                    <button key={o.name} data-testid={`formula-${c.id}-${o.name}`} onClick={() => setCourses((s) => ({ ...s, [c.id]: o.name }))}
                      className={`rounded-full px-4 py-2.5 text-xs font-bold tracking-wide border transition-all active:scale-95 ${active ? "bg-[#123026] text-[#FAF3E0] border-[#123026] shadow-sm" : "bg-white text-[#123026] border-[#EAE5DC] hover:border-[#C69C4A]/60"}`}>
                      {o.name}{o.price ? ` +${eur(o.price)}` : ""}
                    </button>
                  );
                })}
              </div>
            </Section>
          ))}

          {/* Generic option groups */}
          {groups.map((g) => (
            <Section key={g.id} title={g.name} required={g.required}>
              {g.type === "single" ? (
                <div className="flex flex-wrap gap-2">
                  {g.values.map((v) => {
                    const active = single[g.id] === v.name;
                    return (
                      <button key={v.name} data-testid={`opt-${g.id}-${v.name}`} onClick={() => setSingle((s) => ({ ...s, [g.id]: v.name }))}
                        className={`rounded-full px-4 py-2.5 text-xs font-bold tracking-wide border transition-all active:scale-95 ${active ? "bg-[#123026] text-[#FAF3E0] border-[#123026] shadow-sm" : "bg-white text-[#123026] border-[#EAE5DC] hover:border-[#C69C4A]/60"}`}>
                        {v.name}{v.price ? ` +${eur(v.price)}` : ""}
                      </button>
                    );
                  })}
                </div>
              ) : (
                <div className="space-y-2">
                  {g.values.map((v) => {
                    const active = (multi[g.id] || []).includes(v.name);
                    return (
                      <button key={v.name} data-testid={`opt-${g.id}-${v.name}`} onClick={() => toggleMulti(g.id, v.name)}
                        className={`w-full flex items-center justify-between rounded-2xl px-4 py-3.5 border text-left transition-all ${active ? "bg-[#EBF2EE] border-[#123026]" : "bg-white border-[#EAE5DC] hover:border-[#C69C4A]/50"}`}>
                        <span className="flex items-center gap-2.5">
                          <span className={`w-5 h-5 rounded-md grid place-items-center border transition-all ${active ? "bg-[#123026] border-[#123026]" : "border-[#C9CFC9]"}`}>{active && <Check className="w-3.5 h-3.5 text-white" />}</span>
                          <span className="text-sm font-medium text-[#123026]">{v.name}</span>
                        </span>
                        {v.price ? <span className="text-sm font-semibold text-[#C69C4A]">+{eur(v.price)}</span> : null}
                      </button>
                    );
                  })}
                </div>
              )}
            </Section>
          ))}

          <Section title={t.instructions}>
            <Textarea data-testid="product-instructions" value={instructions} onChange={(e) => setInstructions(e.target.value)} placeholder={t.instructionsPlaceholder} className="bg-white border-[#EAE5DC] rounded-2xl resize-none text-sm focus:border-[#C69C4A] focus:ring-1 focus:ring-[#C69C4A]" rows={2} />
          </Section>
        </div>

        {/* Sticky footer */}
        <div className="shrink-0 border-t border-[#EAE5DC] bg-[#FBF9F5]/98 backdrop-blur-md px-6 py-4 flex items-center gap-4 shadow-[0_-4px_20px_rgba(0,0,0,0.04)]">
          <div className="flex items-center gap-2.5 rounded-full border border-[#EAE5DC] bg-white px-2 py-1.5 shadow-sm">
            <button data-testid="qty-decrease" onClick={() => setQty((q) => Math.max(1, q - 1))} className="w-8 h-8 grid place-items-center rounded-full text-[#123026] hover:bg-[#F5F0E6] disabled:opacity-30 transition-colors" disabled={qty <= 1}><Minus className="w-4 h-4" /></button>
            <span data-testid="qty-value" className="w-5 text-center font-bold text-[#123026]">{qty}</span>
            <button data-testid="qty-increase" onClick={() => setQty((q) => q + 1)} className="w-8 h-8 grid place-items-center rounded-full text-[#123026] hover:bg-[#F5F0E6] transition-colors"><Plus className="w-4 h-4" /></button>
          </div>
          <Button data-testid="product-modal-add" onClick={handleAdd} disabled={!canAdd} className="flex-1 h-13 py-3.5 rounded-full bg-[#123026] text-[#FAF3E0] hover:bg-[#1A3D32] font-bold text-sm tracking-wide disabled:opacity-50 transition-all shadow-lg flex items-center justify-center gap-2">
            {canAdd ? `${t.add} · ${eur(unitPrice * qty)}` : (missing[0]?.name ? `Choisir : ${missing[0].name}` : t.add)}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Section({ title, required, children }) {
  return (
    <div>
      <h3 className="text-xs font-bold uppercase tracking-[0.15em] text-[#8A918D] mb-2.5 flex items-center gap-1.5">
        {title}{required && <span className="text-[#C69C4A]">*</span>}
      </h3>
      {children}
    </div>
  );
}
